import {useCallback, useMemo, useRef, useState} from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {Dialog} from 'shells/dialog/dialog';

import {useUnsubscribeContactFromAllAudiences} from '../../../email-marketing/audiences/graphql/hooks/use-unsubscribe-contact-from-all-audiences';
import type {PeepType} from '../../../types';

import {addNotification} from '../../../../../notifications/index';

import {getActionMenuOptions} from './helpers';

interface Props {
    entityType: PeepType;
    entityId: string;
    entityName: string;
    isSoftDeleted: boolean;
    onDelete: () => Promise<any>;
    onUndelete: () => Promise<any>;
    onPermanentlyDelete: () => Promise<any>;
    isUnsubscribedFromAll?: boolean;
    onCustomizeLayout?: () => void;
}

export function PeepHeaderActionMenu(props: Props) {
    const {
        entityName,
        onPermanentlyDelete,
        onDelete,
        onUndelete,
        isUnsubscribedFromAll,
        onCustomizeLayout,
    } = props;

    const {unsubscribeFromAllAudiences} = useUnsubscribeContactFromAllAudiences();

    const [isConfirmingPermanentlyDelete, setIsConfirmingPermanentlyDelete] =
        useState<boolean>(false);
    const [confirmDeleteError, setConfirmDeleteError] = useState<string | undefined>(undefined);
    const [isPermanentlyDeleteSaving, setIsPermanentlyDeleteSaving] = useState<boolean>(false);

    const [isConfirmingUnsubscribeFromAll, setIsConfirmingUnsubscribeFromAll] =
        useState<boolean>(false);
    const [isUnsubscribeFromAllSaving, setIsUnsubscribeFromAllSaving] = useState<boolean>(false);

    const actionMenuRef = useRef<HTMLElement | null | undefined>(null);

    const userFacingEntityType = props.entityType === 'account' ? 'company' : 'person';

    const handlePermanentlyDeleteClick = useCallback(() => {
        setIsConfirmingPermanentlyDelete(true);
    }, []);

    const handleUnsubscribeFromAllClick = useCallback(() => {
        setIsConfirmingUnsubscribeFromAll(true);
    }, []);

    const handlePermanentlyDelete = useCallback(() => {
        setIsPermanentlyDeleteSaving(true);
        onPermanentlyDelete()
            .then(() => {
                setIsConfirmingPermanentlyDelete(false);
                setIsPermanentlyDeleteSaving(false);
            })
            .catch((error) => {
                setConfirmDeleteError(safelyGetGraphQLErrorMessage(error) ?? undefined);
                setIsPermanentlyDeleteSaving(false);
            });
    }, [setIsConfirmingPermanentlyDelete, onPermanentlyDelete]);

    const handleUnsubscribeFromAll = useCallback(() => {
        // Prevent spam clicks firing off a million mutation requests
        // as dialog component has no saving state
        if (isUnsubscribeFromAllSaving) {
            return;
        }

        setIsUnsubscribeFromAllSaving(true);
        unsubscribeFromAllAudiences({
            contactId: props.entityId,
            shouldPrimaryEmailBeSubscribedToAllAudiences: false,
            shouldPrimaryPhoneBeSubscribedToAllAudiences: false,
        })
            .then(() => {
                setIsConfirmingUnsubscribeFromAll(false);
            })
            .catch(() => {
                addNotification('Whoops!', {
                    body: 'Something went wrong. Please try again later.',
                    icon: 'frowny',
                });
            })
            .finally(() => {
                setIsUnsubscribeFromAllSaving(false);
            });
    }, [isUnsubscribeFromAllSaving, unsubscribeFromAllAudiences, props.entityId]);

    const options = useMemo(
        () =>
            getActionMenuOptions(
                props.isSoftDeleted,
                userFacingEntityType,
                onDelete,
                onUndelete,
                handlePermanentlyDeleteClick,
                handleUnsubscribeFromAllClick,
                isUnsubscribedFromAll,
                onCustomizeLayout
            ),
        [
            props.isSoftDeleted,
            handlePermanentlyDeleteClick,
            onDelete,
            onUndelete,
            userFacingEntityType,
            handleUnsubscribeFromAllClick,
            isUnsubscribedFromAll,
            onCustomizeLayout,
        ]
    );

    return (
        <div>
            <ActionMenu
                options={options}
                padding='less'
                size='small'
                hasHoverBackground={true}
                getRef={(ref) => {
                    actionMenuRef.current = ref;
                }}
            />
            {isConfirmingPermanentlyDelete && (
                <ConfirmationDialog
                    title={`Are you sure you want to permanently delete this ${userFacingEntityType}?`}
                    message='This action cannot be undone.'
                    onCancel={() => {
                        setIsConfirmingPermanentlyDelete(false);
                        setConfirmDeleteError(undefined);
                    }}
                    onConfirm={handlePermanentlyDelete}
                    errorMessage={confirmDeleteError}
                    isSaving={isPermanentlyDeleteSaving}
                />
            )}
            {isConfirmingUnsubscribeFromAll && (
                <Dialog
                    location='top'
                    question={`Are you sure you want to unsubscribe ${entityName} from all email and SMS outreach? You won't be able to resubscribe them to any audience.`}
                    primaryText='Yes, unsubscribe'
                    secondaryText='No, changed mind'
                    primaryVariant='danger'
                    onPrimary={handleUnsubscribeFromAll}
                    onSecondary={() => {
                        setIsConfirmingUnsubscribeFromAll(false);
                    }}
                    anchor={actionMenuRef.current}
                />
            )}
        </div>
    );
}
